/**
 * Modal class - Main modal logic
 */

import {DEFAULT_CONFIG, modalStack}                                                                      from './constants.js';
import {on, off, emit}                                                                                   from './events.js';
import {runMiddleware}                                                                                   from './middleware.js';
import {triggerHaptic, waitForAnimation, toggleFullscreen}                                               from './animations.js';
import {scrollBehaviour}                                                                                 from './scroll.js';
import {setFocusToFirstNode, maintainFocus, storeLastFocus, restoreLastFocus, announce, clearModalFocus} from './accessibility.js';
import {
    onSwipeStart,
    onSwipeMove,
    onSwipeEnd,
    onMouseDragStart,
    onMouseDragMove,
    onMouseDragEnd,
    onPullStart,
    onPullMove,
    onPullEnd,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragStartMouse,
    onDragMoveMouse,
    onDragEndMouse
}                                                                                                        from './gestures.js';

export default class Modal {
    constructor(options = {}) {
        // Merge with defaults
        const config = {...DEFAULT_CONFIG, ...options};

        // Save a reference of the modal
        this.modal = document.getElementById(config.targetModal);

        if (!this.modal) {
            if (config.debugMode) {
                console.error(`BaseModal: Modal with id "${config.targetModal}" not found`);
            }
            return;
        }

        // Save config
        this.config = config;

        // Promise resolvers
        this.resolveShow  = null;
        this.resolveClose = null;

        // Auto-close timer
        this.autoCloseTimer = null;

        // Drag to dismiss state
        this.dragStartY = 0;
        this.isDragging = false;

        // Swipe navigation state
        this.isSwiping      = false;
        this.swipeThreshold = 50;

        // Pull to refresh state
        this.pullStartY    = 0;
        this.isPulling     = false;
        this.pullThreshold = 80;
        this.isRefreshing  = false;

        // Lazy loading state
        this.contentLoaded = false;

        // Animation state flags (prevent double open/close during animation)
        this.isClosing = false;
        this.isOpening = false;

        // Event emitter for custom events
        this.events = {};

        // Active element (trigger that opened the modal)
        this.activeElement = null;

        // Register triggers if provided
        if (config.triggers && config.triggers.length > 0) {
            this.registerTriggers(...config.triggers);
        }

        // Pre-bind functions for event listeners
        this.onClick   = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd   = this.onTouchEnd.bind(this);

        // Touch handling state to prevent ghost clicks
        this._touchHandled = false;
    }

    /**
     * Event emitter methods
     */
    on(event, callback) {
        return on(this, event, callback);
    }

    off(event, callback) {
        off(this, event, callback);
    }

    emit(event, data) {
        emit(this, event, data);
    }

    /**
     * Middleware
     */
    async runMiddleware(middlewares, context) {
        return runMiddleware(this, middlewares, context);
    }

    /**
     * Animation methods
     */
    triggerHaptic(type) {
        triggerHaptic(this, type);
    }

    waitForAnimation(element, callback, maxWait) {
        waitForAnimation(this, element, callback, maxWait);
    }

    async toggleFullscreen() {
        return toggleFullscreen(this);
    }

    scrollBehaviour(toggle) {
        scrollBehaviour(this, toggle);
    }

    /**
     * Accessibility methods
     */
    setFocusToFirstNode(focusSelector = undefined) {
        setFocusToFirstNode(this, focusSelector);
    }

    maintainFocus(event) {
        maintainFocus(this, event);
    }

    storeLastFocus(element) {
        storeLastFocus(this, element);
    }

    restoreLastFocus() {
        restoreLastFocus(this);
    }

    restoreFocusableElements() {
        if (!this._focusableElementsWithDisabledFocus || this._focusableElementsWithDisabledFocus.length === 0) {
            return;
        }

        this._focusableElementsWithDisabledFocus.forEach((item) => {
            if (item.originalTabindex !== null) {
                item.element.setAttribute('tabindex', item.originalTabindex);
            } else {
                item.element.removeAttribute('tabindex');
            }
            if (item.originalInert !== null) {
                item.element.setAttribute('inert', item.originalInert);
            } else {
                item.element.removeAttribute('inert');
            }
        });

        this._focusableElementsWithDisabledFocus = [];
    }

    /**
     * Gesture handlers
     */
    onSwipeStart(event) {
        onSwipeStart(this, event);
    }

    onSwipeMove(event) {
        onSwipeMove(this, event);
    }

    onSwipeEnd(event) {
        onSwipeEnd(this, event);
    }

    onMouseDragStart(event) {
        onMouseDragStart(this, event);
    }

    onMouseDragMove(event) {
        onMouseDragMove(this, event);
    }

    onMouseDragEnd(event) {
        onMouseDragEnd(this, event);
    }

    onPullStart(event) {
        onPullStart(this, event);
    }

    onPullMove(event) {
        onPullMove(this, event);
    }

    async onPullEnd(event) {
        return onPullEnd(this, event);
    }

    onDragStart(event) {
        onDragStart(this, event);
    }

    onDragMove(event) {
        onDragMove(this, event);
    }

    onDragEnd(event) {
        onDragEnd(this, event);
    }

    onDragStartMouse(event) {
        onDragStartMouse(this, event);
    }

    onDragMoveMouse(event) {
        onDragMoveMouse(this, event);
    }

    onDragEndMouse(event) {
        onDragEndMouse(this, event);
    }

    /**
     * Register triggers for opening modal
     */
    registerTriggers(...triggers) {
        triggers.filter(Boolean).forEach(trigger => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                this.showModal(event).catch(console.error);
            });
        });
    }

    /**
     * Load content dynamically
     */
    async loadContent() {
        if (this.contentLoaded || !this.config.lazyLoad || !this.config.contentUrl) {
            return;
        }

        const contentContainer = this.modal.querySelector('.bm-modal__content');
        if (!contentContainer) {
            if (this.config.debugMode) {
                console.error('BaseModal: Content container not found');
            }
            return;
        }

        // Show loading state
        const originalContent      = contentContainer.innerHTML;
        contentContainer.innerHTML = '<div class="bm-modal__loading">Loading...</div>';

        try {
            const response = await fetch(this.config.contentUrl);

            if (!response.ok) {
                contentContainer.innerHTML = originalContent;
                // Stop here on error; outer catch will handle logging/emits
                return;
            }

            const html = await response.text();

            // Insert loaded content
            if (html) {
                contentContainer.innerHTML = html;
                this.contentLoaded         = true;

                // Initialize Alpine.js if available
                if (typeof Alpine !== 'undefined' && Alpine.initTree) {
                    Alpine.initTree(contentContainer);
                }

                // Call callback
                this.config.onContentLoaded.call(this, this.modal, html);
                this.emit(
                    'contentLoaded',
                    {
                        modal:this.modal,
                        html
                    }
                );

                if (this.config.debugMode) {
                    console.log('BaseModal: Content loaded successfully');
                }
            }
        } catch (error) {
            if (this.config.debugMode) {
                console.error('BaseModal: Error loading content', error);
            }
            // Restore original content on error
            contentContainer.innerHTML = originalContent;
            this.emit(
                'contentLoadError',
                {
                    modal:this.modal,
                    error
                }
            );
        }
    }

    /**
     * Show modal
     */
    async showModal(event = null) {
        if (!this.modal || !this.config) {
            console.error('BaseModal: Modal instance is not initialized');
            return Promise.reject(new Error('Modal instance is not initialized'));
        }

        // Prevent opening if already opening, closing, or already open
        if (this.isOpening) {
            if (this.config.debugMode) {
                console.log('🚪 showModal: Already opening, ignoring duplicate call');
            }
            return Promise.resolve(this);
        }
        if (this.isClosing) {
            if (this.config.debugMode) {
                console.log('🚪 showModal: Currently closing, wait for close to complete');
            }
            return Promise.resolve(this);
        }
        if (this.modal.classList.contains(this.config.openClass)) {
            if (this.config.debugMode) {
                console.log('🚪 showModal: Already open, ignoring duplicate call');
            }
            return Promise.resolve(this);
        }

        this.isOpening = true;

        return new Promise(async (resolve) => {
            this.resolveShow = resolve;

            // Store the element that triggered the modal
            this.activeElement = event ? event.currentTarget : document.activeElement;
            this.storeLastFocus(this.activeElement);

            // Ensure modal is focusable when opening
            this.modal.removeAttribute('inert');

            // Trigger haptic feedback
            this.triggerHaptic('light');

            // Load content if lazy loading is enabled
            if (this.config.lazyLoad && !this.contentLoaded) {
                await this.loadContent();
            }

            // Run beforeShow middleware
            if (this.config.beforeShow && this.config.beforeShow.length > 0) {
                const context = {
                    modal  :this.modal,
                    trigger:this.activeElement,
                    event,
                    data   :{}
                };

                const canProceed = await this.runMiddleware(this.config.beforeShow, context);

                if (!canProceed) {
                    if (this.config.debugMode) {
                        console.log('BaseModal: showModal interrupted by middleware');
                    }
                    resolve({
                        modal      :this.modal,
                        trigger    :this.activeElement,
                        event,
                        interrupted:true
                    });
                    return;
                }
            }

            // Emit beforeShow event
            this.emit('beforeShow', {
                modal  :this.modal,
                trigger:this.activeElement,
                event
            });

            // Announce to screen readers
            announce('Modal opened');

            // Add to modal stack
            if (this.config.allowNested) {
                // Stack previous modal if exists
                if (modalStack.length > 0) {
                    const previousModal = modalStack[modalStack.length - 1];
                    if (previousModal && previousModal.modal) {
                        previousModal.modal.classList.add('bm-modal--stacked');
                        previousModal.modal.style.setProperty('--modal-stack-index', modalStack.length);

                        // Remove event listeners from previous modal to prevent conflicts
                        if (this.config.debugMode) {
                            console.log('⏸️ Pausing event listeners for stacked modal:', previousModal.modal.id);
                        }
                        previousModal.removeEventListeners();
                    }
                }
                modalStack.push(this);
            }

            // Set accessibility attributes
            this.modal.setAttribute('aria-hidden', 'false');
            if (!this.modal.hasAttribute('role')) {
                this.modal.setAttribute('role', 'dialog');
            }
            this.modal.setAttribute('aria-modal', 'true');

            // Add the openClass to the modal for styling
            this.modal.classList.add(this.config.openClass);

            // Add size class to container
            if (this.config.size && this.config.size !== 'default') {
                const container = this.modal.querySelector('.bm-modal__container');
                if (container) {
                    container.classList.add(`bm-modal__container--${this.config.size}`);
                }
            }

            // Remove old position classes before adding new one
            this.modal.classList.forEach(className => {
                if (className.startsWith('bm-modal--position-')) {
                    this.modal.classList.remove(className);
                }
            });

            // Add position class
            if (this.config.position && this.config.position !== 'center') {
                this.modal.classList.add(`bm-modal--position-${this.config.position}`);
            }

            // Remove old theme classes before adding new one
            this.modal.classList.forEach(className => {
                if (className.startsWith('bm-modal--theme-')) {
                    this.modal.classList.remove(className);
                }
            });

            // Add theme class
            if (this.config.theme) {
                let themeToApply = this.config.theme;

                // Auto theme detection
                if (themeToApply === 'auto') {
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    themeToApply      = prefersDark ? 'dark' : 'light';
                }

                if (themeToApply !== 'auto') {
                    this.modal.classList.add(`bm-modal--theme-${themeToApply}`);
                }
            }

            // Remove old animation classes before adding new one
            this.modal.classList.forEach(className => {
                if (className.startsWith('bm-modal--animation-')) {
                    this.modal.classList.remove(className);
                }
            });

            // Add animation class
            if (this.config.animation) {
                this.modal.classList.add(`bm-modal--animation-${this.config.animation}`);
            }

            // Disable scroll behavior when the modal is open (only for first modal)
            if (modalStack.length === 1) {
                this.scrollBehaviour('disable');
            }

            // Add event listeners for the modal
            this.addEventListeners();

            // Auto-close if enabled
            if (this.config.autoClose && this.config.autoCloseDelay > 0) {
                this.autoCloseTimer = setTimeout(() => {
                    this.closeModal();
                }, this.config.autoCloseDelay);
            }

            // Emit beforeShow event
            this.emit('beforeShow', {
                modal  :this.modal,
                trigger:this.activeElement,
                event
            });

            // Check if awaitOpenAnimation is set in the config
            if (this.config.awaitOpenAnimation) {
                // Wait for animation/transition to complete
                this.waitForAnimation(this.modal, () => {
                    this.isOpening = false;
                    this.setFocusToFirstNode();
                    this.config.onShow(this.modal, this.activeElement, event);
                    this.emit('show', {
                        modal  :this.modal,
                        trigger:this.activeElement,
                        event
                    });
                    if (this.resolveShow) {
                        this.resolveShow(this); // Return modal instance
                    }
                });
            } else {
                // If no animation is awaited, directly set focus to the first node
                this.isOpening = false;
                this.setFocusToFirstNode();
                this.config.onShow(this.modal, this.activeElement, event);
                this.emit('show', {
                    modal  :this.modal,
                    trigger:this.activeElement,
                    event
                });
                if (this.resolveShow) {
                    this.resolveShow(this); // Return modal instance
                }
            }
        });
    }

    /**
     * Close modal
     */
    async closeModal(event = null) {
        // Prevent multiple close calls - check immediately, not after Promise creation
        if (this.isClosing) {
            if (this.config.debugMode) {
                console.log('🚪 closeModal: Already closing, ignoring duplicate call', {
                    modalId  :this.modal.id,
                    isClosing:this.isClosing
                });
            }
            return Promise.resolve();
        }

        this.isClosing = true;

        // Prevent focus inside while closing
        if (this.modal) {
            this.modal.setAttribute('inert', '');
        }

        return new Promise(async (resolve) => {
            if (this.config.debugMode) {
                console.log('🚪 closeModal: START', {
                    modalId            :this.modal.id,
                    classList          :this.modal.className,
                    ariaHidden         :this.modal.getAttribute('aria-hidden'),
                    awaitCloseAnimation:this.config.awaitCloseAnimation
                });
            }

            this.resolveClose = resolve;

            // Trigger haptic feedback on close
            this.triggerHaptic('light');

            // Run beforeClose middleware
            if (this.config.beforeClose && this.config.beforeClose.length > 0) {
                const context = {
                    modal  :this.modal,
                    trigger:this.activeElement,
                    event,
                    data   :{}
                };

                const canProceed = await this.runMiddleware(this.config.beforeClose, context);

                if (!canProceed) {
                    if (this.config.debugMode) {
                        console.log('BaseModal: closeModal interrupted by middleware');
                    }
                    resolve({
                        modal      :this.modal,
                        trigger    :this.activeElement,
                        event,
                        interrupted:true
                    });
                    return;
                }
            }

            // Emit beforeClose event
            this.emit('beforeClose', {
                modal:this.modal,
                event
            });

            // Clear auto-close timer if exists
            if (this.autoCloseTimer) {
                clearTimeout(this.autoCloseTimer);
                this.autoCloseTimer = null;
            }

            // Store a reference to the current context
            const _this   = this;
            const {modal} = _this;

            // Remove from stack
            const stackIndex = modalStack.indexOf(this);
            if (stackIndex > -1) {
                modalStack.splice(stackIndex, 1);
            }

            // Restore previous modal from stack
            if (modalStack.length > 0) {
                const previousModal = modalStack[modalStack.length - 1];
                if (previousModal && previousModal.modal) {
                    previousModal.modal.classList.remove('bm-modal--stacked');
                    previousModal.modal.style.removeProperty('--modal-stack-index');

                    // Re-attach event listeners to previous modal
                    if (_this.config.debugMode) {
                        console.log('🔄 Restoring event listeners for previous modal:', previousModal.modal.id);
                    }
                    previousModal.addEventListeners();
                }
            }

            const finalizeClose = () => {
                modal.classList.remove(_this.config.openClass);
                modal.classList.remove('bm-modal--stacked');
                modal.style.removeProperty('--modal-stack-index');

                _this.removeEventListeners();
                _this.config.onClose(_this.modal, _this.activeElement, event);
                announce('Modal closed');
                _this.emit('close', {
                    modal  :_this.modal,
                    trigger:_this.activeElement,
                    event
                });

                if (modalStack.length === 0) {
                    _this.scrollBehaviour('enable');
                }

                if (_this.resolveClose) {
                    _this.resolveClose({
                        modal  :_this.modal,
                        trigger:_this.activeElement,
                        event
                    });
                }

                _this.isClosing = false;
                _this.isOpening = false;
            };

            // Check if there is an awaitCloseAnimation configuration
            if (this.config.awaitCloseAnimation) {
                if (_this.config.debugMode) {
                    console.log('⏳ closeModal: Waiting for animation to complete...');
                }

                // Clear current focus inside modal and restore previous focus BEFORE setting aria-hidden
                // Prevents aria-hidden warning when focus remains on hidden modal
                clearModalFocus(_this);
                if (_this.config.backFocus) {
                    _this.restoreLastFocus();
                }

                // Save original values to restore them later
                this._focusableElementsWithDisabledFocus = [];
                const focusableSelector                  = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
                const focusableElements                  = _this.modal.querySelectorAll(focusableSelector);
                focusableElements.forEach((el) => {
                    this._focusableElementsWithDisabledFocus.push({
                        element         :el,
                        originalTabindex:el.getAttribute('tabindex'),
                        originalInert   :el.getAttribute('inert')
                    });
                    el.setAttribute('tabindex', '-1');
                    el.setAttribute('inert', '');
                });

                // Also blur active element if it's inside modal
                if (document.activeElement && (_this.modal.contains(document.activeElement) || document.activeElement.closest(`[${this.config.closeTrigger}]`))) {
                    document.activeElement.blur();
                    document.body.focus({preventScroll:true});
                }

                // Set aria-hidden to trigger close animation
                if (_this.config.debugMode) {
                    console.log('🚪 closeModal: Setting aria-hidden="true" to start animation', {
                        modalId  :_this.modal.id,
                        classList:_this.modal.className
                    });
                }
                modal.setAttribute('aria-hidden', 'true');
                modal.removeAttribute('aria-modal');

                // Wait for animation/transition to complete
                this.waitForAnimation(this.modal, () => {
                    if (_this.config.debugMode) {
                        console.log('🎯 closeModal: Animation complete, cleanup starting', {
                            modalId  :_this.modal.id,
                            openClass:_this.config.openClass,
                            classList:_this.modal.className
                        });
                    }

                    finalizeClose();

                    _this.restoreFocusableElements();
                });
            } else {
                // Clear current focus inside modal and restore previous focus BEFORE setting aria-hidden
                clearModalFocus(_this);
                if (_this.config.backFocus) {
                    _this.restoreLastFocus();
                }

                // Set aria-hidden to trigger close animation
                if (_this.config.debugMode) {
                    console.log('🚪 closeModal: Setting aria-hidden="true"', {
                        modalId  :_this.modal.id,
                        classList:_this.modal.className
                    });
                }
                modal.setAttribute('aria-hidden', 'true');
                modal.removeAttribute('aria-modal');

                finalizeClose();

                _this.restoreFocusableElements();
            }
        });
    }

    /**
     * Add event listeners
     */
    addEventListeners() {
        // Add touch event listeners to prevent ghost clicks on mobile
        this.modal.addEventListener('touchstart', this.onTouchStart, {passive:true});
        this.modal.addEventListener('touchend', this.onTouchEnd, {passive:false});

        // Add click event listener to the modal
        this.modal.addEventListener('click', this.onClick);

        // Add keydown event listener to the document
        document.addEventListener('keydown', this.onKeydown);

        // Add drag to dismiss event listeners if enabled
        if (this.config.dragToDismiss) {
            // Touch events
            this.modal.addEventListener('touchstart', this.onDragStart.bind(this), {passive:true});
            this.modal.addEventListener('touchmove', this.onDragMove.bind(this), {passive:true});
            this.modal.addEventListener('touchend', this.onDragEnd.bind(this), {passive:true});

            // Mouse events for desktop
            this.modal.addEventListener('mousedown', this.onDragStartMouse.bind(this));
            document.addEventListener('mousemove', this.onDragMoveMouse.bind(this));
            document.addEventListener('mouseup', this.onDragEndMouse.bind(this));
        }

        // Add swipe navigation event listeners if enabled
        if (this.config.swipeNavigation) {
            const content = this.modal.querySelector('.bm-modal__content');
            if (content) {
                // Touch events for mobile
                content.addEventListener('touchstart', this.onSwipeStart.bind(this), {passive:true});
                content.addEventListener('touchmove', this.onSwipeMove.bind(this), {passive:true});
                content.addEventListener('touchend', this.onSwipeEnd.bind(this), {passive:true});

                // Mouse events for desktop
                content.addEventListener('mousedown', this.onMouseDragStart.bind(this));
                content.addEventListener('mousemove', this.onMouseDragMove.bind(this));
                content.addEventListener('mouseup', this.onMouseDragEnd.bind(this));
                content.addEventListener('mouseleave', this.onMouseDragEnd.bind(this));
            }
        }

        // Add pull to refresh event listeners if enabled
        if (this.config.pullToRefresh) {
            const content = this.modal.querySelector('.bm-modal__content');
            if (content) {
                content.addEventListener('touchstart', this.onPullStart.bind(this), {passive:true});
                content.addEventListener('touchmove', this.onPullMove.bind(this), {passive:false});
                content.addEventListener('touchend', this.onPullEnd.bind(this), {passive:true});
            }
        }
    }

    /**
     * Remove event listeners
     */
    removeEventListeners() {
        if (this.modal) {
            this.modal.removeEventListener('touchstart', this.onTouchStart);
            this.modal.removeEventListener('touchend', this.onTouchEnd);
            this.modal.removeEventListener('click', this.onClick);
        }
        document.removeEventListener('keydown', this.onKeydown);
    }

    /**
     * Handle touch start - track that we're handling a touch event
     */
    onTouchStart(event) {
        // Store the touch target for later comparison
        this._touchTarget = event.target;
        this._touchHandled = false;
    }

    /**
     * Handle touch end - prevent ghost click after modal close
     */
    onTouchEnd(event) {
        // Check if the touch ended on a close trigger
        const closeElement = event.target.closest(`[${this.config.closeTrigger}]`);

        if (closeElement) {
            // Mark that we've handled this touch to prevent click handler
            this._touchHandled = true;

            // Prevent default to stop ghost click
            event.preventDefault();
            event.stopPropagation();

            // Use a small delay to ensure any pending click events are consumed
            setTimeout(() => {
                this.closeModal(event).catch(console.error);
            }, 0);
        }
    }

    /**
     * Handle click events
     */
    onClick(event) {
        // If this click was already handled by touch, ignore it
        if (this._touchHandled) {
            this._touchHandled = false;
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        if (this.config.debugMode) {
            console.log('🖱️ onClick:', {
                target      :event.target,
                closeTrigger:this.config.closeTrigger,
                hasAttribute:event.target.hasAttribute(this.config.closeTrigger),
                closest     :event.target.closest(`[${this.config.closeTrigger}]`)
            });
        }

        // Check if the clicked element or its parent has the close trigger attribute
        const closeElement = event.target.closest(`[${this.config.closeTrigger}]`);

        if (closeElement) {
            if (this.config.debugMode) {
                console.log('✅ Close trigger found, closing modal');
            }
            // Prevent ghost click on touch devices
            event.preventDefault();
            event.stopPropagation();
            this.closeModal(event).catch(console.error);
        }
    }

    /**
     * Handle keyboard events
     */
    onKeydown(event) {
        // Check custom keyboard shortcuts first
        if (this.config.keyboardShortcuts && Object.keys(this.config.keyboardShortcuts).length > 0) {
            const key      = event.key;
            const shortcut = this.config.keyboardShortcuts[key];

            if (shortcut && typeof shortcut === 'function') {
                // Only trigger for topmost modal
                if (modalStack.length > 0 && modalStack[modalStack.length - 1] === this) {
                    event.preventDefault();
                    shortcut.call(this, this.modal, event);
                    return;
                }
            }
        }

        // Check if the Escape key is pressed and disableEsc is false
        if (event.key === 'Escape' && !this.config.disableEsc) {
            // Only close topmost modal
            if (modalStack.length > 0 && modalStack[modalStack.length - 1] === this) {
                this.closeModal(event).catch(console.error);
            }
        }

        // Check if the Tab key is pressed and maintain focus within modal
        if (event.key === 'Tab') {
            this.maintainFocus(event);
        }
    }

    /**
     * Destroy modal instance
     */
    destroy() {
        // Remove from stack if present
        const stackIndex = modalStack.indexOf(this);
        if (stackIndex > -1) {
            modalStack.splice(stackIndex, 1);
        }

        // Remove event listeners
        this._touchHandled = false;
        this.removeEventListeners();

        // Clear timers
        if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
        }

        // Remove all custom event listeners
        this.events = {};

        if (this.config && this.config.debugMode) {
            console.log('BaseModal: Instance destroyed', this.modal ? this.modal.id : 'unknown');
        }
    }
}
